import { jsxs, jsx } from "react/jsx-runtime";
import React__default, { memo, useRef, useMemo, useEffect } from "react";
import { ChatMessage } from "./ChatMessage.js";
const isUIMessage = (msg) => "id" in msg && "parts" in msg && Array.isArray(msg.parts);
function extractTextContent(parts) {
  if (!parts) return "";
  return parts.filter((p) => p.type === "text" && "text" in p).map((p) => p.text).join("");
}
const SharedThreadDivider = ({ text }) => /* @__PURE__ */ jsxs(
  "div",
  {
    style: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "4px 0",
      margin: "8px 0"
    },
    children: [
      /* @__PURE__ */ jsx("div", { style: { flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.12)" } }),
      /* @__PURE__ */ jsx(
        "span",
        {
          style: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.4)",
            whiteSpace: "nowrap"
          },
          children: text
        }
      ),
      /* @__PURE__ */ jsx("div", { style: { flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.12)" } })
    ]
  }
);
const MessageListV2 = memo(
  ({
    conversationId,
    messages,
    showTypingIndicator,
    streamingAIResponse,
    isActivelyStreaming,
    transientStatus,
    streamingStoreLocator,
    streamingDevInfo,
    followUpQuestions = [],
    latestFollowUps = [],
    isLoadingFollowUps = false,
    bottomRef,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    onRetryLastMessage,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    onOpenShareDialog
  }) => {
    const listRef = useRef(null);
    const prevMinHeightElRef = useRef(null);
    const displayMessages = useMemo(
      () => [
        ...messages,
        ...showTypingIndicator ? [
          {
            role: "assistant",
            content: "",
            timestamp: /* @__PURE__ */ new Date(),
            isTyping: true
          }
        ] : [],
        // Pending assistant response indicator (polling for backend response)
        ...isPendingAssistantResponse && !isPollingTimedOut && !showTypingIndicator ? [
          {
            role: "assistant",
            content: "",
            timestamp: /* @__PURE__ */ new Date(),
            isTyping: true
          }
        ] : [],
        // Add a streaming message only when streaming text exists.
        ...streamingAIResponse ? [
          {
            role: "assistant",
            content: streamingAIResponse,
            timestamp: /* @__PURE__ */ new Date(),
            // Only mark as streaming if we're still actively streaming
            // Once streaming stops, show actions even if we're still showing streamingAIResponse
            isStreaming: isActivelyStreaming
          }
        ] : []
      ],
      [
        messages,
        showTypingIndicator,
        streamingAIResponse,
        isActivelyStreaming,
        isPendingAssistantResponse,
        isPollingTimedOut
      ]
    );
    const hasInteractedRef = useRef(false);
    useEffect(() => {
      if (isActivelyStreaming) hasInteractedRef.current = true;
    }, [isActivelyStreaming]);
    useEffect(() => {
      hasInteractedRef.current = false;
    }, [conversationId]);
    useEffect(() => {
      const listEl = listRef.current;
      if (!listEl || displayMessages.length === 0) {
        if (prevMinHeightElRef.current) {
          prevMinHeightElRef.current.style.minHeight = "";
          prevMinHeightElRef.current.style.marginBottom = "";
          prevMinHeightElRef.current = null;
        }
        return;
      }
      const scrollContainer = listEl.closest('[role="log"]');
      if (!scrollContainer) return;
      const apply = () => {
        if (prevMinHeightElRef.current) {
          prevMinHeightElRef.current.style.minHeight = "";
          prevMinHeightElRef.current.style.marginBottom = "";
        }
        const allMessages = listEl.querySelectorAll(".message.user, .message.assistant");
        const lastMsg = allMessages[allMessages.length - 1];
        if (!lastMsg) return;
        if (hasInteractedRef.current) {
          const viewportH = scrollContainer.clientHeight;
          const allUserMessages = listEl.querySelectorAll(".message.user");
          const lastUser = allUserMessages[allUserMessages.length - 1];
          const userBubbleH = lastUser ? lastUser.offsetHeight : 0;
          lastMsg.style.minHeight = viewportH - userBubbleH - 54 + "px";
        } else {
          lastMsg.style.marginBottom = "50px";
        }
        prevMinHeightElRef.current = lastMsg;
      };
      apply();
      const mo = new MutationObserver(apply);
      mo.observe(listEl, { childList: true });
      const ro = new ResizeObserver(apply);
      ro.observe(scrollContainer);
      return () => {
        mo.disconnect();
        ro.disconnect();
        if (prevMinHeightElRef.current) {
          prevMinHeightElRef.current.style.minHeight = "";
          prevMinHeightElRef.current.style.marginBottom = "";
          prevMinHeightElRef.current = null;
        }
      };
    }, [displayMessages.length, isPollingTimedOut]);
    const topDividerText = isSharedView ? sharedByUserName ? `Shared with you by ${sharedByUserName}` : "Shared with you" : sourceConversationId ? "Your copy of a shared conversation" : null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: listRef,
        style: {
          width: "100%",
          maxWidth: "768px",
          display: "flex",
          flexDirection: "column",
          gap: "24px"
        },
        children: [
          topDividerText && /* @__PURE__ */ jsx(SharedThreadDivider, { text: topDividerText }),
          displayMessages.map((message, displayIndex) => {
            const hasStreamingMessage = !!streamingAIResponse;
            const isLastMessage = displayIndex === messages.length + (showTypingIndicator ? 1 : 0) + (hasStreamingMessage ? 1 : 0) - 1;
            const isTyping = "isTyping" in message && message.isTyping;
            const isExplicitlyStreaming = "isStreaming" in message && message.isStreaming;
            const isLastUiMessageStreaming = isActivelyStreaming && !isExplicitlyStreaming && isUIMessage(message) && message.role === "assistant" && displayIndex === messages.length - 1;
            const isStreaming = isExplicitlyStreaming || isLastUiMessageStreaming;
            const showStreamingStoreLocator = isStreaming && streamingStoreLocator;
            const messageKey = isTyping ? "typing-indicator" : isUIMessage(message) && message.id ? `msg-${message.id}` : `message-${displayIndex}`;
            const messageContent = extractTextContent(message.parts);
            const messageUiParts = message.parts;
            const messageFollowUpQueries = isLastMessage && message.role === "assistant" ? latestFollowUps : isUIMessage(message) ? message.parts.filter((p) => p.type === "data-follow-ups").flatMap((p) => {
              var _a;
              return ((_a = p.data) == null ? void 0 : _a.questions) ?? [];
            }) : [];
            const showFollowUpQueries = !isTyping && !isStreaming && message.role === "assistant" && messageFollowUpQueries.length > 0;
            const showFollowUpsLoading = isLastMessage && !isTyping && !isStreaming && message.role === "assistant" && isLoadingFollowUps && latestFollowUps.length === 0;
            const showForkBoundary = sourceConversationId && sourceCheckpointIndex !== void 0 && !isTyping && !isStreaming && displayIndex === sourceCheckpointIndex + 1;
            return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
              showForkBoundary && /* @__PURE__ */ jsx(SharedThreadDivider, { text: "Your messages below — only visible to you" }),
              /* @__PURE__ */ jsx(
                ChatMessage,
                {
                  bottomRef: isLastMessage ? bottomRef : void 0,
                  role: message.role,
                  content: messageContent,
                  isTyping,
                  isStreaming,
                  isLastMessage,
                  conversationId: conversationId || void 0,
                  messageIndex: "messageIndex" in message ? message.messageIndex : !isTyping && !isStreaming ? displayIndex : void 0,
                  feedback: "feedback" in message ? message.feedback : void 0,
                  followUpQueries: showFollowUpQueries ? messageFollowUpQueries : [],
                  nextUserMessage: (() => {
                    const nextMsg = messages[displayIndex + 1];
                    if (displayIndex >= messages.length - 1 || (nextMsg == null ? void 0 : nextMsg.role) !== "user") return void 0;
                    return extractTextContent(nextMsg.parts);
                  })(),
                  storeLocator: "storeLocator" in message ? message.storeLocator : void 0,
                  streamingStoreLocator: showStreamingStoreLocator ? streamingStoreLocator : void 0,
                  orders: "orders" in message ? message.orders : void 0,
                  devInfo: isStreaming && streamingDevInfo ? streamingDevInfo : "devInfo" in message ? message.devInfo : void 0,
                  onSearch,
                  onUpdateFeedback,
                  onVariantSelect,
                  onSuggestionClick,
                  apiBaseUrl,
                  isSharedView,
                  uiMessageParts: messageUiParts,
                  transientStatus: isStreaming ? transientStatus : void 0,
                  pdpStacklineSku,
                  isOnChatWidget,
                  brandLogoUrl,
                  onRetry: isLastMessage && message.role === "assistant" ? onRetryLastMessage : void 0,
                  onOpenShareDialog,
                  followUpsLoading: showFollowUpsLoading
                }
              )
            ] }, messageKey);
          }),
          isPollingTimedOut && onRetry && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
                padding: "24px 16px"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "p",
                  {
                    style: {
                      fontSize: "14px",
                      color: "rgba(0,0,0,0.5)",
                      textAlign: "center",
                      margin: 0
                    },
                    children: "Response generation timed out. Please try again."
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: onRetry,
                    style: {
                      padding: "8px 24px",
                      borderRadius: "20px",
                      border: "1px solid rgba(0,0,0,0.2)",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "rgba(0,0,0,0.7)"
                    },
                    children: "Retry"
                  }
                )
              ]
            }
          )
        ]
      }
    );
  }
);
export {
  MessageListV2
};
//# sourceMappingURL=MessageListV2.js.map
